import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { loucaApi } from '../services/loucaApi';
import { useAuth } from '../contexts/AuthContext';
import { normalizeAnalysisWorkflow, getWorkflowBadge } from '../utils/analysisWorkflow';

const InspectionsList = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      setLoading(true);
      // Utiliser l'API V9 pour lister les sessions
      const response = await loucaApi.listSessionsV3();

      if (response.status === 'success' && response.sessions) {
        // Convertir les sessions V9 au format attendu par le composant
        const inspections = response.sessions.map(session => ({
          inspection_id: session.session_id,
          status: session.status || 'unknown',
          created_at: session.created_at,
          updated_at: session.completed_at || session.created_at,
          car_info: {
            license_plate: session.session_id.replace('V9_', '').substring(0, 8)
          },
          chunks_processed: session.chunks_processed || 0,
          total_frames_analyzed: session.total_frames_analyzed || 0,
          total_damages: session.total_damages || 0
        }));
        setInspections(inspections);
      }
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
      console.error('Error loading inspections:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'processing':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      default:
        return 'bg-slate-700 text-slate-400 border-slate-600';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const resolveVersionLabel = (inspection) => {
    const workflowModel = normalizeAnalysisWorkflow(inspection);
    return getWorkflowBadge(workflowModel);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-24">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">Mes Inspections</h1>
          <button
            onClick={() => navigate('/upload')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition-all"
          >
            + Nouvelle Inspection
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {inspections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400 mb-4">Aucune inspection pour le moment</p>
            <button
              onClick={() => navigate('/upload')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition-all"
            >
              Créer votre première inspection
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {inspections.map((inspection) => (
              <div
                key={inspection.inspection_id}
                onClick={() => navigate(`/inspection/${inspection.inspection_id}`)}
                className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-blue-500/50 transition cursor-pointer"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {inspection.car_info?.license_plate || inspection.inspection_id}
                    </h3>
                    <p className="text-sm text-slate-400">ID : {inspection.inspection_id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                        inspection.status
                      )}`}
                    >
                      {inspection.status}
                    </span>
                    <span className="px-3 py-1 rounded-full text-sm font-semibold border border-blue-500/50 text-blue-300 bg-blue-500/10">
                      {resolveVersionLabel(inspection)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                  {inspection.created_at && (
                    <span className="flex items-center gap-2">
                      <span className="text-slate-500 uppercase tracking-wide text-xs">Créée</span>
                      <span className="text-slate-200 font-medium">
                        {formatDate(inspection.created_at)}
                      </span>
                    </span>
                  )}
                  {inspection.updated_at && (
                    <span className="flex items-center gap-2">
                      <span className="text-slate-500 uppercase tracking-wide text-xs">
                        MAJ
                      </span>
                      <span className="text-slate-300">
                        {formatDate(inspection.updated_at)}
                      </span>
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/40 rounded-full text-xs text-blue-200">
                    <span className="uppercase tracking-wide text-blue-300">Version</span>
                    <span className="text-blue-100 font-semibold">
                      {resolveVersionLabel(inspection)}
                    </span>
                  </span>
                </div>

                {inspection.comment && (
                  <div className="mt-3 text-sm text-slate-200 bg-slate-700/40 border border-slate-600 px-4 py-3 rounded-lg">
                    <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      Commentaire utilisateur
                    </p>
                    <p>{inspection.comment}</p>
                  </div>
                )}

                {inspection.car_info?.license_plate && (
                  <div className="mt-2 text-sm text-slate-300">
                    Plaque :{' '}
                    <span className="text-white font-semibold">
                      {inspection.car_info.license_plate}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InspectionsList;



